import _ from 'lodash';
import { permissionsRequiredForModules, reactModules } from 'src/data/modules';

/**
 * Extract just the permissive endpoint paths from the JWT.
 * @param {object} data - contents of JWT
 * @returns {object} - formatted endpoints.
 */
export const obtainPermissionsFromJWT = (data) => {
  const permissionsRaw = _.pickBy(data, (value, key) =>
    key.includes('INCLUDE'),
  );
  const formattedPaths = _.mapKeys(permissionsRaw, (val, key) => {
    // as there is no guarantee that the "scope" will always be GLOBAL we need to grab it to use it
    const scope = key.match(/INCLUDE\/(\w+)(?:\/)?/)?.[1] ?? null;
    // make sure to display an error on the FE when an unexpected scope is seen
    console.assert(scope === 'GLOBAL', `UNEXPECTED SCOPE IN JWT: ${scope}`);
    // remove standard beginning of path
    const path = key.split(`INCLUDE/${scope}`)[1];
    if (!path) {
      return scope;
    }
    return key.split(`INCLUDE/${scope}`)[1].substring(1);
  });

  return _.pickBy(formattedPaths);
};

/**
 * Define if the given required permission exists as a child or parent
 *
 * @param {object} requiredPermission - permission to check against
 * @param {object} permissionsPaths - all permissive endpoints
 * @returns {boolean}
 */
export const hasPermission = (requiredPermission, permissionsPaths) => {
  const globalPermissions = permissionsPaths.GLOBAL;
  let foundPermission = '';
  let loopCount = 0;

  if (globalPermissions) {
    if (
      globalPermissions.includes(requiredPermission.opType) ||
      globalPermissions.includes(requiredPermission.oneOfType)
    ) {
      return true;
    }
  }

  const loop = (permission) => {
    if (!permission) {
      return;
    }
    // Check that it is a parent, parents won't have braces,
    // If it's a child remove last path until parent found.
    if (loopCount && permission) {
      const split = permission.split('/');
      if (split[split.length - 1].includes('{')) {
        split.pop();
        loopCount += 1;
        loop(split.join('/'));
      }
    }
    if (permissionsPaths[permission]) {
      foundPermission = permission;
    } else {
      // If not found - go back a path, and check again recursively.
      const split = permission.split('/');
      split.pop();
      loopCount += 1;
      loop(split.join('/'));
    }
  };
  loop(requiredPermission.path);

  return (
    foundPermission &&
    ((requiredPermission.opType &&
      requiredPermission.opType
        .split('')
        .every((opTypeVal) =>
          permissionsPaths[foundPermission].includes(opTypeVal),
        )) ||
      (requiredPermission.oneOfType &&
        requiredPermission.oneOfType
          .split('')
          .some((opTypeVal) =>
            permissionsPaths[foundPermission].includes(opTypeVal),
          )))
  );
};

/**
 *
 * @param {object} jwtData
 * @returns {object} path - is allowed or not.
 */
export const formatUserPermissions = (jwtData) => {
  // Not logged in, so allow permission to anything pre-loggin that doesn't have
  // any entries in the requredPermissions.
  // We wouldn't have a way to decipher it's permissions as no JWT so we are just
  // going off if there are any entries.
  if (!Object.keys(jwtData)) {
    return reactModules
      .filter((module) => !permissionsRequiredForModules[module.ref])
      .reduce((acc, module) => ({ ...acc, [module.ref]: true }), {});
  }

  const permissionPaths = obtainPermissionsFromJWT(jwtData);
  const allModules = Object.keys(_.mapKeys(reactModules, (value) => value.ref));
  const allowedModules = allModules.reduce((acc, module) => {
    const foundRequiredPermission = permissionsRequiredForModules[module];
    if (!foundRequiredPermission) {
      return { ...acc, [module]: true };
    }
    if (foundRequiredPermission && !foundRequiredPermission.length) {
      return { ...acc, [module]: false };
    }

    return {
      ...acc,
      [module]: foundRequiredPermission.every((path) =>
        hasPermission(path, permissionPaths),
      ),
    };
  }, {});

  return allowedModules;
};
